import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Container, Stack } from '@fiverr-private/layout_components';
import { GigDescription, GigInfoHeader, GigInfoHeaderProps, GigMetadata } from '@fiverr-private/gig_info';
import { SellerCard } from '@fiverr-private/gig_seller_card';
import { NotableClients } from '@fiverr-private/gig_notable_clients';
import { SellerHighlightsList } from '@fiverr-private/gig_seller_highlights';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { VettedByProBanner } from '@fiverr-private/gig_page_banners';
import { ReviewsSnippet } from '@fiverr-private/gig_reviews';
import type { ReviewAttributes, Review, ReviewsSnippet as ReviewsSnippetType } from '@fiverr-private/gig_page_types';
import { Waypoint, ABOUT_SELLER, ABOUT_THIS_GIG, RECOMMENDATIONS } from '@fiverr-private/gig_page_waypoint_navigation';
import { Ugc } from '../../../types/ugc';
import { Seller } from '../../../types/seller';
import { GIG_SUB_HEADER } from '../../../utils/events';
import { prepareSellerCard } from '../../../utils/dataFormatters/prepareSellerCard';
import { prepareSellerHighlights } from '../../../utils/dataFormatters/prepareSellerHighlights';
import { prepareGigDescription } from '../../../utils/dataFormatters/prepareGigDescription';
import { prepareGigMetadata } from '../../../utils/dataFormatters/prepareGigMetadata';
import { prepareNotableClientsProps } from '../../../utils/dataFormatters/prepareNotableClientsProps';
import { metric } from '../../../utils/metric';
import { ProBadge } from '../../shared/ProBadge/ProBadge';
import Workflow from '../../Workflow';
import WorkProcess from '../../WorkProcess';
import Faq from '../../Faq';
import Reviews from '../../Reviews';
import Tags from '../../Tags';
import PackagesTable from '../../PackagesTable';
import OrderDetails from '../../OrderDetails';
import Gallery from '../../Gallery';
import Overview from '../../Overview';
import Portfolio from '../../Portfolio';
import { OutOfOffice } from '../../OutOfOffice/OutOfOffice';
import { Disclaimer } from '../../Disclaimer/Disclaimer';
import { MoreServices } from '../../MoreServices/MoreServices';
import UGCTranslateButton from '../../UGCTranslateButton';
import PersonalizationWrapper from '../../PersonalizationWrapper';
import { SECTION_NAMES } from '../../PersonalizationWrapper/constants';
import { ExpertList } from '../../ExpertList/ExpertList';
import { LayoutProps } from '../gigThemesConfig';
import { useOnReviewsClick } from '../../../hooks/useOnReviewsClick';
import { UpmarketBanners } from '../../UpmarketBanners';
import { expertListContainerStyle, proRebrandingStyle, sellerCardStyle } from './NonExperientialGigPage.ve.css';

interface NonExperientialGigPageProps extends LayoutProps {
  ugc: Ugc;
  seller: Seller;
}

const NonExperientialGigPageComponent = (props: NonExperientialGigPageProps) => {
  const {
    overview,
    repeatScore,
    gallery,
    reviews,
    workflow,
    tags,
    info,
    shouldRenderUGCTranslateButton,
    portfolioProps,
    useStickyLayout,
    paymentType,
    shouldRenderComparePersonalizedSection,
    ugc,
    seller,
    sellerCard,
    choiceEligibilities,
    description,
    notableClients,
    summary,
  } = props;

  const shouldRenderReviews = props.shouldRenderReviews();
  const {
    seller: { isAgency },
    sellerUsername,
    general,
    biEvents,
    sellerLanguageParams,
    pathfinderWrapper,
    isBusinessLayout,
    isProRebrandingLayout,
    gig: { isRestrictedByRegion },
  } = useGigPageContext();

  const onReviewsClick = useOnReviewsClick();

  const shouldRenderNewProBanner = props.shouldRenderNewProBanner();
  const upmarketBannerVariants = props.getUpmarketBannerVariants();
  const summaryMode = props.getAISummaryMode();

  const gigMetadataProps = prepareGigMetadata({ description });

  const sellerCardProps = prepareSellerCard({
    seller,
    sellerCard,
    sellerLanguageParams,
    pathfinderWrapper,
    ugc,
    sellerUsername,
    gigMetadata: gigMetadataProps.gigMetadata,
    general,
    overview,
    onReviewsClick,
    isProRebrandingLayout,
    summary,
    summaryMode,
  });

  const { highlights, onHighlightsImpression } = prepareSellerHighlights({
    seller,
    sellerCard,
    repeatScore,
    choiceEligibilities,
    isBusinessLayout,
    isProRebrandingLayout,
    biEvents,
  });
  const shouldRenderHighlights = highlights.length > 0 && !shouldRenderNewProBanner;

  const showGigInfoProBadge = isProRebrandingLayout && general.isPro;
  const { proSubCategories: proSubcategories } = sellerCard;

  const gigInfoTheme = isBusinessLayout ? 'business' : 'default';
  const gigDescriptionProps = prepareGigDescription({ description, ugc });

  const { notableClients: adaptedNotableClients } = prepareNotableClientsProps({ notableClients });

  const gigInfoHeaderProps = {
    biEvents,
    theme: gigInfoTheme,
    badge: showGigInfoProBadge ? <ProBadge /> : null,
  } satisfies GigInfoHeaderProps;

  const sellerCardClassNames = classNames('seller-card', sellerCardStyle, {
    [proRebrandingStyle]: isProRebrandingLayout,
  });

  const reviewsSnippetAttributes: ReviewAttributes = {
    reviews: (reviews as Record<string, Review[]>).reviews,
    reviewSnippets: (reviews as Record<string, ReviewsSnippetType[]>).reviews_snippet,
  };

  return (
    <>
      <OutOfOffice />
      <Overview
        overview={overview}
        studioInfo={info}
        useStickyLayout={useStickyLayout}
        repeatScore={repeatScore}
        isRestrictedByRegion={isRestrictedByRegion}
      />

      {shouldRenderUGCTranslateButton && <UGCTranslateButton componentName={GIG_SUB_HEADER} />}

      <Waypoint source={ABOUT_SELLER} />
      <Stack className={sellerCardClassNames}>
        <SellerCard metric={metric} {...sellerCardProps} />
      </Stack>

      {shouldRenderNewProBanner && <VettedByProBanner seller={{ displayName: seller.user.profile.displayName }} />}

      {isProRebrandingLayout && (
        <Container className={classNames('expert-list', expertListContainerStyle)}>
          <ExpertList proSubcategories={proSubcategories} isFoldable columnCount={3} />
        </Container>
      )}

      {shouldRenderHighlights && (
        <Stack className="highlights" gap="4" direction="column">
          <SellerHighlightsList highlights={highlights} isAgency={isAgency} onImpression={onHighlightsImpression} />
        </Stack>
      )}

      <ReviewsSnippet reviewAttributes={reviewsSnippetAttributes} isLimitedEdition={general.isLimitedEdition} />

      <Waypoint source={ABOUT_THIS_GIG} />
      <Stack gap="8" direction="column" className="about-gig">
        <Stack gap="4" direction="column">
          <GigInfoHeader {...gigInfoHeaderProps} />
          <GigDescription {...gigDescriptionProps} biEvents={biEvents} />
        </Stack>
        <Gallery gallery={gallery} isFullView={false} />
        <GigMetadata {...gigMetadataProps} biEvents={biEvents} />
      </Stack>

      <Disclaimer />

      <div className="notable-clients">
        <NotableClients biEvents={biEvents} isAgency={isAgency} notableClients={adaptedNotableClients} />
      </div>

      <Portfolio {...portfolioProps} />

      <Workflow workflow={workflow} />
      <WorkProcess paymentType={paymentType} />

      <PackagesTable />
      <OrderDetails />

      <Waypoint source={RECOMMENDATIONS} />
      {shouldRenderComparePersonalizedSection() && (
        <PersonalizationWrapper sectionName={SECTION_NAMES.COMPARE} className="personalized-content-compare" />
      )}

      <MoreServices />
      <Faq />

      <UpmarketBanners upmarketBannerVariants={upmarketBannerVariants} />

      {shouldRenderReviews && <Reviews reviews={reviews} />}

      <Tags tags={tags} />
    </>
  );
};

const mapStateToProps = ({ ugc }) => ({ ugc });

export const NonExperientialGigPage = connect(mapStateToProps, null)(NonExperientialGigPageComponent);
